// ─────────────────────────────────────────────────────────────────────
// BUG REPORT HELPERS — pure text builders used by BugReportModal (and
// BugDetailModal, which reuses buildClaudePrompt). Extracted verbatim from the
// monolith (legacy/App.jsx). No secrets here.
//
//   getPageName(view)            → human-readable Norwegian page name
//   buildClaudePrompt(...)       → a prompt describing the bug/feature
//   buildEmailBody(...)          → the e-mail body (includes captured console log)
//
// buildEmailBody pulls the session console log from ../../lib/consoleCapture
// (getCapturedConsoleLog). PORTAL_BRAND comes from ../../data/brand.
//
// Types are intentionally loose (any), matching the monolith's untyped style.
// ─────────────────────────────────────────────────────────────────────

import { PORTAL_BRAND } from '../../data/brand';
import { getCapturedConsoleLog } from '../../lib/consoleCapture';
import {
  getCapturedActions,
  getFailedRequests,
  formatActions,
  formatFailedRequests,
} from '../../lib/activityCapture';
import { APP_VERSION } from '../../data/changelog';
import { Storage } from '../../lib/storage';

// Environment facts that a reporter cannot be expected to type, but that decide
// how a bug reproduces. The viewport is the big one: layout/overflow bugs (a
// modal clipped by the sticky header, a table overflowing) only appear at
// certain sizes, and without it a fix starts by guessing the screen size.
/**
 * Query parameters that carry a credential, stripped from a report's URL before
 * it leaves the browser (#1080) — the invitation token (#1069) today. The API
 * strips the same list again (`apps/api/src/bugs/report.ts`), and a test holds
 * the two equal, so a new link-borne token is added to both on purpose.
 */
export const CREDENTIAL_URL_PARAMS = ['invitation'] as const;

/** The address with every credential parameter removed; anything else kept. */
export function redactCredentialParams(href: string): string {
  try {
    const url = new URL(href);
    let changed = false;
    for (const name of CREDENTIAL_URL_PARAMS) {
      if (url.searchParams.has(name)) {
        url.searchParams.delete(name);
        changed = true;
      }
    }
    return changed ? url.toString() : href;
  } catch {
    return href.replace(/([?&])invitation=[^&#]*&?/g, '$1').replace(/[?&]$/, '');
  }
}

export function collectRuntimeInfo(): Record<string, any> {
  if (typeof window === 'undefined') return {};
  const nav: any = typeof navigator !== 'undefined' ? navigator : {};
  const ua = String(nav.userAgent || '');
  // Deliberately coarse — enough to spot an engine/OS difference, no fingerprinting.
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'ukjent';
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /Android/.test(ua)
      ? 'Android'
      : /iPhone|iPad|iPod/.test(ua)
        ? 'iOS'
        : /Mac OS X/.test(ua)
          ? 'macOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : 'ukjent';
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    /* ignore — not worth failing a bug report over */
  }
  let backend = 'ukjent';
  try {
    backend = (Storage as any)?.backend || 'ukjent';
  } catch {
    /* ignore */
  }
  return {
    appVersion: APP_VERSION,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    skjerm:
      typeof screen !== 'undefined' && screen?.width ? `${screen.width}x${screen.height}` : null,
    pikselforhold: window.devicePixelRatio || 1,
    nettleser: browser,
    os,
    storageBackend: backend,
    språk: nav.language || null,
    tidssone: tz || null,
    online: nav.onLine !== false,
  };
}

// Get a human-readable name for the view
export function getPageName(view: any): string {
  const names: any = {
    home: 'Forside',
    wizard: 'Opprett Padel24-turnering',
    tournament: 'Padel24-turnering (aktiv)',
    players: 'Spillerdatabase',
    archive: 'Aktivitetsarkiv',
    reporting: 'Reporting',
    messages: 'Meldinger',
    engine: `Om ${PORTAL_BRAND.engineName}`,
    otherFormats: 'Alle turneringer',
    singleMatches: 'Enkeltkamper',
    americanoSetup: 'Americano-oppsett',
    americano: 'Americano (aktiv)',
    login: 'Innlogging',
  };
  return names[view] || view || 'Ukjent side';
}

// Build a prompt for Claude based on the context
export function buildClaudePrompt(
  pageName: any,
  view: any,
  context: any,
  title: any,
  description: any,
  kind: any = 'bug',
  // Everything needed to act on the report in ONE paste. `runtime` defaults to the
  // live environment, `consoleLog` to the session capture — so an old report that
  // stored neither still yields a useful prompt.
  extras: {
    runtime?: any;
    consoleLog?: any;
    hasScreenshot?: boolean;
    actions?: any;
    failedRequests?: any;
  } = {},
): string {
  const kindLabel = kind === 'feature' ? 'Funksjonsforslag for' : 'Bug i';
  let prompt = `${kindLabel} ${PORTAL_BRAND.name}-appen.\n\n`;
  prompt += `Side: ${pageName} (view='${view}')\n`;
  prompt += `Tittel: ${title}\n`;
  prompt += `Beskrivelse: ${description}\n`;

  if (context && Object.keys(context).length > 0) {
    prompt += `\nKontekst:\n`;
    for (const [k, v] of Object.entries(context)) {
      prompt += `  ${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}\n`;
    }
  }

  // Environment — the reporter can't type this, and it decides how a bug repros.
  const runtime =
    extras.runtime && Object.keys(extras.runtime).length ? extras.runtime : collectRuntimeInfo();
  if (runtime && Object.keys(runtime).length > 0) {
    prompt += `\nMiljø:\n`;
    for (const [k, v] of Object.entries(runtime)) {
      if (v == null || v === '') continue;
      prompt += `  ${k}: ${v}\n`;
    }
  }

  if (extras.hasScreenshot) {
    prompt += `\nSkjermbilde: ja — bruk «kopiér skjermbilde» i bug-arkivet og lim det inn rett etter denne teksten.\n`;
  }

  // Console log — previously only in the e-mail body, so a copied prompt arrived
  // without the one thing that usually pinpoints a crash.
  const log = extras.consoleLog !== undefined ? extras.consoleLog : getCapturedConsoleLog();
  if (log) {
    const text = String(log).trim();
    if (text) {
      // Keep the tail: the newest lines are the ones around the failure.
      const MAX = 4000;
      const clipped = text.length > MAX ? `…(forkortet)…\n${text.slice(-MAX)}` : text;
      prompt += `\nKonsoll-logg fra økten:\n${clipped}\n`;
    }
  }

  // What the user did just before, and which API calls failed — the trail to
  // the bug. Stored on the report; defaults to the live capture like the log.
  const actions = formatActions(
    extras.actions !== undefined ? extras.actions : getCapturedActions(),
  );
  if (actions) prompt += `\nSiste handlinger (eldst først):\n${actions}\n`;
  const failed = formatFailedRequests(
    extras.failedRequests !== undefined ? extras.failedRequests : getFailedRequests(),
  );
  if (failed) prompt += `\nFeilede kall:\n${failed}\n`;

  if (kind === 'feature') {
    prompt += `\nVennligst foreslå hvordan denne funksjonen kan implementeres i henhold til kodebasen sine etablerte mønstre.`;
  } else {
    prompt += `\nVennligst finn årsaken og foreslå en fix.`;
  }

  // Add page-specific hints
  const sideHints: any = {
    tournament:
      'Sjekk recordResult-funksjonen i TournamentView for ELO-håndtering. Husk teamElo1Pre/playerEloPre per kamp.',
    americano:
      'Sjekk AmericanoView og americanoEngine.generateRound. Algoritmen er i 01a_americano_engine.js.',
    americanoSetup: 'Sjekk AmericanoSetupView og hvordan config sendes til engine.create().',
    singleMatches:
      'Sjekk addSingleMatch/updateSingleMatch/deleteSingleMatch i 02_app.js. Husk pre-ELO lagring.',
    players: 'Sjekk PlayersView og getEffectivePlayerDB i engine.',
    archive:
      'Sjekk ArchiveView og combinedArchive i 02_app.js (inkluderer virtual single_matches).',
    wizard: 'Sjekk Wizard step1-5 og hvordan tournament-state bygges opp.',
  };

  if (sideHints[view]) {
    prompt += `\n\nHint: ${sideHints[view]}`;
  }

  return prompt;
}

// Build the email body with all info
export function buildEmailBody(
  pageName: any,
  title: any,
  description: any,
  claudePrompt: any,
  kind: any = 'bug',
): string {
  const date = new Date().toLocaleString('no-NO');
  const headerLabel = kind === 'feature' ? 'FUNKSJONSFORSLAG' : 'BUG-RAPPORT';
  const consoleLog = getCapturedConsoleLog();
  return `${headerLabel} fra ${PORTAL_BRAND.name}-appen

Dato: ${date}
Side: ${pageName}
Type: ${kind === 'feature' ? 'Feature request' : 'Bug'}

TITTEL:
${title}

BESKRIVELSE:
${description}

${
  consoleLog
    ? `---

KONSOLL-LOGG (siste feilmeldinger fra denne sesjonen):
${consoleLog}
`
    : ''
}---

PROMPT TIL CLAUDE (kopiér og lim inn for å fikse):

${claudePrompt}

---

(Rapporten er også lagret i appens bug-arkiv lokalt.)`;
}
